/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { Logger } from '@core/lib/services';
import { ProxyDataService } from '@app/core/http';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from '@core/lib/services/message.service';
import { VirtualGridColumn, VirtualGridTemplate } from '@wfm/virtual-grid';
import { AppConfigService } from '@app/common/app-config.service';
import { UtilService } from '@app/util/util.service';

const log = new Logger('LayoutService');

@Injectable({
  providedIn: 'root'
})
export class LayoutService {
  private attributeCache = new Map();
  private attributePreloadCache = new Map();
  private cellTemplates;
  private allAttributeCachev8 = [];
  private userWorklistLayoutMapv8 = new Map();
  private isLayoutLoadedSuccessfully = false;
  private v8WorklistTemplateMapCache = new Map();
  // Layout Templates
  private v8Templates = new Map();
  private layoutsMap = new Map();
  private layoutsMapByName = new Map();
  //All Attributes[All possible viewable columns ]
  private v8AttributeNameIdCache = new Map<string, string>();
  // Set for each New worklist SElected
  private activeWLColumns;
  private activeWLColumnsAttrIds: string;
  private activeWLColumnsFilterAttrIds: string;

  constructor(
    private dataService: ProxyDataService,
    private translate: TranslateService,
    private messageService: MessageService,
    private appConfigService: AppConfigService,
    private utilService: UtilService
  ) {
    this.broadcastListener();
  }

  init(): Promise<any> {
    return new Promise((resolve, reject) => {
      Promise.all([this.v8getAllLayoutEncodings(), this.getAllAttributes(), this.v8GetWorklistUserLayoutMap()])
        .then(() => {
          resolve();
        })
        .catch(reason => {
          log.error('Error occurred while getting templates and worklist layouts', reason);
          reject(reason);
        });
    });
  }

  broadcastListener() {
    this.messageService.on('customWorklistRefresh').subscribe(worklists => {
      if (worklists.length > 0) {
        this.cacheWorklistTemplateMap(worklists, 'Custom');
      }
    });

    this.messageService.on('defaultWorklistLoaded').subscribe(worklists => {
      if (worklists) {
        this.cacheWorklistTemplateMap(worklists, 'System');
      }
    });

    this.messageService.on('todoworklists').subscribe(worklists => {
      if (worklists.length > 0) {
        this.cacheWorklistTemplateMap(worklists, 'System');
      }
    });

    this.messageService.on('allWorklistRefresh').subscribe(worklists => {
      if (worklists.length > 0) {
        this.cacheWorklistTemplateMap(worklists, 'System');
      }
    });

    this.messageService.on('savedWorkListsLoaded').subscribe(worklists => {
      if (worklists.length > 0) {
        this.cacheWorklistTemplateMap(worklists, 'Folders');
      }
    });
  }

  getLayoutByName(layoutName: string) {
    if (this.layoutsMapByName.has(layoutName)) {
      const templateId = this.layoutsMapByName.get(layoutName);
      return this.v8Templates.get(templateId);
    }
  }

  getv8AllWorklistLayoutFromCache(): any[] {
    return Array.from(this.v8WorklistTemplateMapCache).map(value => value[1]);
  }

  getv8AllTemplates(): any[] {
    return Array.from(this.v8Templates).map(value => value[1]);
  }

  getAutoSaveTemplateByGlobalTemplateId(globalTemplateId: string) {
    const autoSaveTemplateName = this.layoutsMap.get(globalTemplateId.toString());
    if (autoSaveTemplateName) {
      if (this.layoutsMapByName.has(autoSaveTemplateName + '_u$er')) {
        const autoSaveTemplateId = this.layoutsMapByName.get(autoSaveTemplateName + '_u$er');
        return this.v8Templates.get(autoSaveTemplateId);
      }
    }
    return;
  }

  getAttributeFromCache(attributeId) {
    const attrProp = this.attributeCache.get(attributeId);
    return _.isUndefined(attrProp) ? {} : attrProp;
  }

  getPreloadAttributeListFromCache() {
    return _.pluck(this.attributePreloadCache.get('preloadList'), 'name');
  }

  getAllAttributeListFromCache() {
    return this.allAttributeCachev8;
  }

  /* used  for Prefetching Filters  and making filter calls */
  setActiveLayoutColumns(activecolumnNames: any) {
    this.activeWLColumns = activecolumnNames;
  }

  getLayoutFilterAttributeListFromCacheNames() {
    return this.activeWLColumns;
  }

  setActiveWLColumnsAttrId(allActiveAttrIds: any) {
    this.activeWLColumnsAttrIds = allActiveAttrIds;
  }

  getActiveWLColumnsAttrId() {
    return this.activeWLColumnsAttrIds;
  }

  getLayoutFilterAttributeListFromCache() {
    return this.activeWLColumnsFilterAttrIds;
  }

  setLayoutFilterAttributeList(activeMultiSelectFilterIds: any) {
    this.activeWLColumnsFilterAttrIds = activeMultiSelectFilterIds;
  }

  setCellTemplates(cellTemplates: any) {
    this.cellTemplates = cellTemplates;
  }

  private cellValueFormatters;

  setCellValueFormatters(cellValueFormatters: any) {
    this.cellValueFormatters = cellValueFormatters;
  }

  createTemplateLayout(allAttrs: any, attrs: any, customization: any, disableFilterSort: boolean) {
    let allAttrIds = '';
    let multiselectFilterList = '';

    const columnDefs = [];

    let isIndicatorAdded = false;

    // Shown Columns
    attrs &&
      attrs.forEach(attrId => {
        if (attrId === 'Indicators') {
          isIndicatorAdded = true;
          columnDefs.push(this.createIndicators(false, customization[attrId]));
        }
        const attr = this.getAttributeFromCache(attrId);
        if (attrId != undefined && attr?.attrId != undefined) {
          if (attr.attrId != 'Indicators') {
            // Disable filtering on columns
            if (attrId === 'verificationTicker') {
              attr.filterable = attrs.indexOf('status') > 1 ? 'false' : 'true';
            }

            // setup for filters
            allAttrIds = allAttrIds + attr.attrId + ',';
            if (attr.filterable && _.contains(['multi-select'], attr.filterType)) {
              multiselectFilterList = multiselectFilterList + attr.attrId + ',';
            }
            columnDefs.push(this.createGridColumn(attr, attrId, false, customization[attrId], disableFilterSort));
          }
        }
      });

    if (!isIndicatorAdded) {
      columnDefs.push(this.createIndicators(true, null));
    }

    // Hidden Columns
    allAttrs &&
      allAttrs.forEach(attrId => {
        const attr = this.getAttributeFromCache(attrId);
        if (attrId != undefined && attr?.attrId != undefined) {
          if (attr.attrId != 'Indicators' && attrs.indexOf(attrId)) {
            if (attr.attrId != 'Indicators') {
              const hidden = attrs.indexOf(attrId) <= -1;
              if (hidden) {
                columnDefs.push(this.createGridColumn(attr, attrId, true, customization[attrId], disableFilterSort));
              }
            } else {
              columnDefs.push(this.createIndicators(true, customization[attrId]));
            }
          }
        }
      });

    this.setActiveLayoutColumns(attrs);
    allAttrIds = allAttrIds.slice(0, -1);
    multiselectFilterList = multiselectFilterList.slice(0, -1);
    this.setActiveWLColumnsAttrId(allAttrIds);
    this.setLayoutFilterAttributeList(multiselectFilterList);

    return [...columnDefs];
  }

  private createIndicators(hidden: boolean, customization: any) {
    let width: number;
    width = undefined;
    let responsive = 2;
    if (customization && customization.width) {
      width = Number(customization.width);
      responsive = undefined;
    }

    return {
      id: 'icons',
      propertyIdentifier: 'Indicators',
      propertyName: 'Indicators',
      headerLabel: this.translate.instant('workloadManager.listContainer.label.indicators'),
      cellTemplate: this.getCellTemplate('icons'),
      minWidth: 65,
      width: width,
      maxWidth: 250,
      isHidden: hidden,
      isPinnable: false,
      isResizable: true,
      responsiveSize: responsive,
      cellStyleClasses: ['indicators'],
      headerStyleClasses: ['indicators']
    } as VirtualGridColumn;
  }

  private createGridColumn(attr: any, attrId, hidden: boolean, customization: any, disableFilterSort: boolean) {
    let width: number;
    let minwidth: number;
    width = undefined;
    let responsive = 1;
    if (customization && customization.width) {
      width = Number(customization.width);
      responsive = undefined;
      log.debug('Applied Customization' + attrId, customization);
    }
    if (attrId === 'assignee' || attrId === 'clinicalComment' || attrId === 'procedureText') {
      responsive = 2;
    }

    minwidth = undefined;
    if (
      attrId === 'verificationTimeOfExam' ||
      attrId === 'timeOfExam' ||
      attrId === 'acquisitionDate' ||
      attrId === 'scheduledDate' ||
      attrId === 'patientArrivalTime' ||
      attrId === 'patientDoB' ||
      attrId === 'lastMenstruationDate'
    ) {
      minwidth = 80;
    }

    const gridColumn = {
      id: attr.attrId,
      propertyIdentifier: attrId,
      propertyName: attr.dbParamName,
      headerLabel: this.getHeaderLabel(attrId),
      aliasHeaderLabel: attr.headerLabel,
      isDraggable: true,
      isPinnable: false,
      isResizable: true,
      width: width,
      minWidth: minwidth,
      responsiveSize: responsive,
      isHidden: hidden,
      valueFormatter: this.getCellValueFormatter(attrId),
      cellTemplate: this.getCellTemplate(attrId)
    } as VirtualGridColumn;

    // Filter & Sort Configuration
    if (!disableFilterSort) {
      // Sort - Defaults to ascending
      if (attr.sortable === 'true') gridColumn.defaultSort = 'ascending';

      // Filter - Add to column defn so it can be updated else exclude
      if (attr.filterable === 'true') gridColumn.filterConfig = undefined;
    }
    if (attr.cellStyleClasses) {
      const cellStyleClasses = attr.cellStyleClasses.split(',').map(s => s.trim());
      gridColumn.cellStyleClasses = cellStyleClasses;
    }

    if (attr.headerStyleClasses) {
      const headerStyleClasses = attr.headerStyleClasses.split(',').map(s => s.trim());
      gridColumn.headerStyleClasses = headerStyleClasses;
    }

    return gridColumn;
  }

  private getHeaderLabel(attrId) {
    const key = 'workloadManager.listContainer.label.' + attrId;
    if (attrId === 'nhi') {
      return this.utilService.getLocalizedNHISpecificText(key, this.appConfigService.getNHIType());
    } else {
      return this.translate.instant(key);
    }
  }

  private getCellTemplate(cellName: string) {
    return this.cellTemplates[cellName];
  }

  private getCellValueFormatter(cellName: string) {
    return this.cellValueFormatters[cellName];
  }

  private v8GetWorklistUserLayoutMap(): Promise<any> {
    const endpoint = 'admin/worklist/v8/userlayouts';
    return this.dataService.get(endpoint).then(
      (data: any) => {
        this.userWorklistLayoutMapv8 = new Map();
        data &&
          data.forEach(element => {
            this.userWorklistLayoutMapv8.set(element.worklistId.toString(), element);
          });
      },
      error => {
        this.logError(endpoint, error);
      }
    );
  }

  private v8getAllLayoutEncodings(): Promise<any> {
    const endpoint = 'admin/worklist/layoutByUser'; //layoutByUser    layout Feb 8
    return this.dataService.get(endpoint).then(
      (data: any) => {
        data &&
          data.forEach(element => {
            const temp = this.templateLayout(element);
            this.v8Templates.set(element.id.toString(), temp);
            this.layoutsMap.set(element.id.toString(), element.name);
            this.layoutsMapByName.set(element.name, element.id.toString());
          });
      },
      error => {
        this.logError(endpoint, error);
      }
    );
  }

  addNewTemplateToCache(id: any, newtemplate: any) {
    const template = this.templateLayout(newtemplate);
    this.v8Templates.set(id.toString(), template);
    this.layoutsMap.set(id.toString(), newtemplate.name);
    this.layoutsMapByName.set(newtemplate.name, id.toString());
    return template;
  }

  v8UpdateLayoutAfterCustomization(
    layoutEncodingObj: any,
    layoutId: number,
    layoutName: string,
    isGlobalTemplate: boolean,
    userName: any,
    isLayoutExisting: boolean,
    saveGlobalAsUser: boolean
  ) {
    const endpoint = 'admin/worklist/layout';
    const layoutJson = this.createLayoutEncodingJsonAfterCustomization(
      layoutEncodingObj,
      layoutId,
      layoutName,
      isGlobalTemplate,
      userName,
      isLayoutExisting,
      saveGlobalAsUser
    );

    log.info('************ User LayoutAttributes', layoutJson);

    return this.dataService.createList(endpoint, layoutJson).catch(error => {
      this.logError(endpoint, error);
    });
  }

  private createLayoutEncodingJsonAfterCustomization(
    layoutEncodingObj: any,
    layoutid: any,
    layoutName: any,
    isGlobalTemplate: boolean,
    userName: any,
    isLayoutExisting: boolean,
    saveGlobalAsUser: boolean
  ) {
    const flag = saveGlobalAsUser ? '7' : '5';

    try {
      log.info('Before Save Existing Layout', this.v8Templates.get(layoutid.toString()).template);

      let layoutJson;
      if (!isLayoutExisting) {
        layoutJson = {
          name: layoutName,
          layout: JSON.stringify(layoutEncodingObj),
          flags: flag,
          userName: userName,
          globalTemplate: false
        };
      } else {
        layoutJson = {
          id: layoutid,
          name: layoutName,
          layout: JSON.stringify(layoutEncodingObj),
          flags: flag,
          userName: userName,
          globalTemplate: false
        };
      }

      log.info('After Save of Grid Customization', layoutJson);

      return layoutJson;
    } catch (ex) {
      log.error('Layout Template Creation Failure', ex);
    }
  }

  private createLayoutEncodingJsonForSave(
    layoutEncodingJson: any,
    layoutid: any,
    layoutName: any,
    userName: any,
    isGlobalTemplate: boolean,
    isLayoutNew: boolean,
    flag: any
  ) {
    const shownColumn = [];
    for (let i = 0; i < layoutEncodingJson.length; i++) {
      shownColumn.push(layoutEncodingJson[i].propertyIdentifier);
    }

    let layout;
    let layoutObj;
    try {
      log.info('Before Save EXisting Layout', this.v8Templates.get(layoutid.toString()).template);
      layout = this.v8Templates.get(layoutid.toString()).template;
      layoutObj = JSON.parse(layout);

      const updatedCustomizations = {};
      let attr;
      for (let i = 0; i < shownColumn.length; i++) {
        attr = shownColumn[i].toString();
        if (layoutObj.customization[attr]) {
          updatedCustomizations[attr] = layoutObj.customization[attr];
        } else {
          // new addition
          updatedCustomizations[attr] = {};
        }
      }

      layoutObj.customization = updatedCustomizations;
      layoutObj.allColumnsAttributes = shownColumn;
      // User Layout 5
      let worklistFlag = '5';
      if (isGlobalTemplate) {
        // Global Layout 3
        worklistFlag = '3';
      }
      if (Number(flag) === 7) {
        worklistFlag = '7';
        isGlobalTemplate = false;
      }

      let layoutJson;
      if (isLayoutNew) {
        layoutJson = {
          name: layoutName,
          layout: JSON.stringify(layoutObj),
          flags: worklistFlag,
          userName: userName,
          globalTemplate: isGlobalTemplate
        };
      } else {
        layoutJson = {
          id: layoutid,
          name: layoutName,
          layout: JSON.stringify(layoutObj),
          flags: worklistFlag,
          userName: userName,
          globalTemplate: isGlobalTemplate
        };
      }
      log.info('Layout Json for Save or Update', layoutJson);
      return layoutJson;
    } catch (ex) {
      log.error('Layout Template Creation Failure', ex);
    }
  }

  /*  Layout Update */
  v8UpdateExistingLayoutEncoding(
    layoutEncodingJson: Array<VirtualGridColumn>,
    layoutid: any,
    layoutName: any,
    userName: any,
    isGlobalTemplate: boolean,
    flag: any
  ) {
    const endpoint = 'admin/worklist/layout';

    const layoutJson = this.createLayoutEncodingJsonForSave(
      layoutEncodingJson,
      layoutid,
      layoutName,
      userName,
      isGlobalTemplate,
      false,
      flag
    );

    return this.dataService.createList(endpoint, layoutJson).catch(error => {
      this.logError(endpoint, error);
    });
  }

  v8UpdateLayoutEncoding(
    layoutEncodingJson: Array<VirtualGridColumn>,
    userName: any,
    layoutName: string,
    isGlobalTemplate: boolean,
    flag: any
  ) {
    const endpoint = 'admin/worklist/layout';

    // Us System Default to save a new Layout
    const layoutJson = this.createLayoutEncodingJsonForSave(
      layoutEncodingJson,
      1,
      layoutName,
      userName,
      isGlobalTemplate,
      true,
      flag
    );

    return this.dataService.createList(endpoint, layoutJson).catch(error => {
      this.logError(endpoint, error);
    });
  }

  v8applyTemplateToWorklists(worklistLayoutMapjson: Array<number>, layoutId: number, userid: string) {
    const endpoint = 'admin/worklist/v8/userlayouts';
    const jsonPayload = [];

    let worklistlayoutmap: any;
    worklistLayoutMapjson.forEach(worklistId => {
      worklistlayoutmap = this.userWorklistLayoutMapv8.get(worklistId.toString());

      if (worklistlayoutmap != undefined) {
        worklistlayoutmap.defaultLayoutId = layoutId;
        jsonPayload.push(this.createPayload(worklistlayoutmap, layoutId));
        log.info('Update User Layout ', worklistlayoutmap);
      } else {
        jsonPayload.push(this.createNewPayload(worklistId, layoutId, userid));
        log.info('New User Layout ', worklistlayoutmap);
      }
    });

    return this.dataService.createList(endpoint, jsonPayload).then(
      (data: any) => {
        this.updateUserLayoutMap(worklistLayoutMapjson, layoutId, true);
        data &&
          data.forEach(element => {
            this.userWorklistLayoutMapv8.set(element.worklistId.toString(), element);
            log.info('User Layout Update', element);
          });
      },
      error => {
        this.logError(endpoint, error);
      }
    );
  }

  v8applyCommonTemplateToWorklists(worklistIds: Array<number>, layoutId: number, userid: string) {
    const endpoint = 'admin/worklist/v8/defaultlayouts';
    const jsonPayload = [];
    for (let i = 0; i < worklistIds.length; i++) {
      const defaultsLayoutPost = {
        id: worklistIds[i],
        layoutId: layoutId,
        userId: userid
      };
      jsonPayload.push(defaultsLayoutPost);
    }

    return this.dataService.createList(endpoint, jsonPayload).then(
      () => {
        this.updateUserLayoutMap(worklistIds, layoutId, false);
      },
      error => {
        this.logError(endpoint, error);
      }
    );
  }

  getUserOrUsersGlobalTemplate(newSelectedTemplate: any, isGlobalTemplate: boolean) {
    if (!isGlobalTemplate) {
      return newSelectedTemplate;
    }
    // Tricky Part making the user just see the Global Layouts
    let autoSaveGlobalTemplate;
    if (newSelectedTemplate.isGlobalTemplate) {
      autoSaveGlobalTemplate = this.getAutoSaveTemplateByGlobalTemplateId(newSelectedTemplate.templateId);
    }

    if (autoSaveGlobalTemplate) {
      // Indirection user will see only Global selected but has the user version applied if present
      return autoSaveGlobalTemplate;
    }

    // return nothing to initiate a new users copy of global template
    return newSelectedTemplate;
  }

  usersSystemTemplate(newSelectedTemplate: any, isGlobalTemplate: boolean) {
    if (!isGlobalTemplate) {
      //its users template send back
      return newSelectedTemplate;
    }
    // Tricky Part making the user just see the Global Layouts
    let autoSaveGlobalTemplate;
    // Previously user saved global template
    if (newSelectedTemplate.isGlobalTemplate) {
      autoSaveGlobalTemplate = this.getAutoSaveTemplateByGlobalTemplateId(newSelectedTemplate.templateId);
    }

    return autoSaveGlobalTemplate;
  }

  autoSaveGlobalLayoutWithCustomization(
    _launchTemplate: VirtualGridTemplate,
    _updatedTemplate: any,
    currentUser: string
  ) {
    log.info('[auto saveLayoutCustomization] Launch LayoutId to be Updated ', _launchTemplate.templateId);
    log.info('[auto saveLayoutCustomization] launch Layout From Cache ', _launchTemplate.template);
    log.info('[auto saveLayoutCustomization]  Customization Updated for User Layout', _updatedTemplate);

    const autoSavedGlobalLayoutDTO = this.usersSystemTemplate(_launchTemplate, _launchTemplate.isGlobalTemplate);

    if (autoSavedGlobalLayoutDTO) {
      // Update the layout
      autoSavedGlobalLayoutDTO.isGlobalTemplate = false;
      return this.v8UpdateLayoutAfterCustomization(
        _updatedTemplate,
        autoSavedGlobalLayoutDTO.templateId,
        autoSavedGlobalLayoutDTO.templateName,
        autoSavedGlobalLayoutDTO.isGlobalTemplate,
        currentUser,
        true,
        true
      );
    } else {
      // templateId will be the pa
      // Use System template
      return this.v8UpdateLayoutAfterCustomization(
        _updatedTemplate,
        _launchTemplate.templateId,
        _launchTemplate.templateName + '_u$er',
        false,
        currentUser,
        false,
        true
      );
    }
  }

  private updateUserLayoutMap(worklistIds, layoutId, isUser: boolean) {
    worklistIds.forEach(worklistId => {
      const worklist = this.v8WorklistTemplateMapCache.get(worklistId);
      if (isUser) {
        worklist.currentTemplate[0] = layoutId;
        worklist.currentTemplate[1] = this.layoutsMap.get(layoutId.toString());
        worklist.hasUserLayout = true;
      } else {
        worklist.defaultTemplate[0] = layoutId;
        worklist.defaultTemplate[1] = this.layoutsMap.get(layoutId.toString());
      }
      log.info('Before Common', worklist);
      this.v8WorklistTemplateMapCache.set(worklistId, worklist);
      log.info('Before Common', this.v8WorklistTemplateMapCache.get(worklistId));
    });
  }

  private createNewPayload(worklistId: number, templateLayoutid: number, userName: string) {
    return {
      worklistId: worklistId,
      defaultLayoutId: templateLayoutid,
      userName: userName,
      worklist_user_meta: ''
    };
  }

  private createPayload(element: any, layoutid: number) {
    if (element.userLayoutId > 0) {
      return {
        userLayoutId: element.userLayoutId.toString(),
        worklistId: element.worklistId.toString(),
        defaultLayoutId: layoutid.toString(),
        userName: element.userName.toString(),
        worklist_user_meta: ''
      };
    }
  }

  templateLayout(element: any) {
    let isGlobal;
    let ico;
    if (Number(element.flags) === 3) {
      ico = 'ico-group-16';
      isGlobal = true;
    } else {
      ico = 'ico-user-16';
      isGlobal = false;
    }

    return {
      templateId: element.id,
      templateName: element.name,
      isGlobalTemplate: isGlobal, // using isGlobal For Layout Setting
      globalTemplate: isGlobal, // Outgoing to Database uses this
      template: element.layout,
      userName: element.userName,
      flags: Number(element.flags),
      icon: ico
    };
  }

  private getAllAttributes(): Promise<any> {
    const endpoint = 'admin/worklist/attributes';
    const preloadList = [];

    let dbParamName = '',
      headerLabel = '',
      keyLabel = '';
    return this.dataService.get(endpoint).then(
      (data: any) => {
        this.allAttributeCachev8 = [];
        _.each(data, (item, key) => {
          if (item.filterable === 'true' && _.contains(['multi-select', 'single-select'], item.filterType)) {
            preloadList.push({
              id: item.attrId,
              name: key
            });
          }

          this.attributeCache.set(key, item);
          if (key != undefined) {
            this.allAttributeCachev8.push(key);
            this.v8AttributeNameIdCache.set(key, item.attrId);
          }
          dbParamName = dbParamName + ',' + item.attrId;
          headerLabel = headerLabel + ',' + item.headerLabel;
          keyLabel = keyLabel + ',' + key;
        });

        this.attributePreloadCache.set('preloadList', preloadList);
      },
      error => {
        this.logError(endpoint, error);
      }
    );
  }

  isLayoutLoaded() {
    return this.isLayoutLoadedSuccessfully;
  }

  cacheWorklistTemplateMap(allWorklistMeta, worklistType) {
    for (let _i = 0; _i < (allWorklistMeta?.length || 0); _i++) {
      const worklist = allWorklistMeta[_i];
      let layoutid = -1;
      let layoutName = '';
      let defLayoutId = 0;
      let defLayoutName = '';
      let hasUserLayoutMap: boolean;
      hasUserLayoutMap = false;

      // Fill in user Layouts based on seperate call
      if (this.userWorklistLayoutMapv8.has(worklist.id.toString())) {
        layoutid = this.userWorklistLayoutMapv8.get(worklist.id.toString()).defaultLayoutId;
        layoutName = this.layoutsMap.get(layoutid.toString());
        hasUserLayoutMap = true;
      }

      if (worklist.layoutId > 0) {
        defLayoutId = worklist.layoutId;
        defLayoutName = this.layoutsMap.get(defLayoutId.toString()); //defLayoutId;
      }

      const wl = {
        worklistId: Number(worklist.id) || Number(worklist.worklistId),
        worklistName: worklist.displayName || worklist.name,
        worklistType: worklistType,
        isGlobalTemplate: true,
        defaultTemplate: [defLayoutId, defLayoutName],
        currentTemplate: [layoutid, layoutName],
        hasUserLayout: hasUserLayoutMap,
        isSelected: false
      };

      log.info('All Worklists Cache', wl);
      this.v8WorklistTemplateMapCache.set(Number(wl.worklistId), wl);
    }

    this.messageService.broadcast('completedLayouts');
  }

  private logError(endpoint, err) {
    log.error(["Error with DataService get call '", endpoint, "':"].join(''), JSON.stringify(err));
  }
}
